import { DynamicModuleService } from "@/services/APIs";
import { removeMetaFields } from "@/lib/removeMetaFields";
import { provisionSchoolLoginAccount } from "@/project/karuna/utils/schoolTenantUser";
import {
  DEFAULT_API_FETCH_CONCURRENCY,
  mapWithConcurrency,
} from "@/project/karuna/utils/mapWithConcurrency";
import { mapExcelRowToFormValues } from "@/project/karuna/utils/schoolExcelOperations";
import {
  persistSchoolImportAuditRecord,
  type SchoolImportAuditRecord,
} from "@/project/karuna/utils/schoolImportAudit";

export const SCHOOL_PROFILE_SCHEMA_NAME = "School Profile";

export type ImportRowAction = "created" | "updated";

export type ImportRowStatus = "SUCCESS" | "FAILED";

export interface ImportRowLog {
  row_number: number;
  status: ImportRowStatus;
  /** Present for SUCCESS rows; omitted when the row failed to import. */
  action?: ImportRowAction;
  record_id?: string;
  /** Reason the row could not be imported (FAILED rows only). */
  error_message?: string;
  mapped_values: Record<string, unknown>;
  source_row: Record<string, unknown>;
}

export interface ImportSummary {
  import_batch_id: string;
  total_rows: number;
  success_count: number;
  created_count: number;
  updated_count: number;
  failed_count: number;
  started_at: string;
  finished_at: string;
}

export interface ImportBatchResult {
  summary: ImportSummary;
  success_logs: ImportRowLog[];
  /** Rows that could not be imported, each with an error_message reason. */
  failed_logs: ImportRowLog[];
  audit_records: SchoolImportAuditRecord[];
}

export interface ExistingSchoolRef {
  id: string;
  school_name: string;
  contact_number: string;
  kc_no: string;
}

export interface ProcessSchoolImportOptions {
  schemaName?: string;
  excelRows: Record<string, unknown>[];
  columnMapping: Record<string, string>;
  existingSchools: ExistingSchoolRef[];
  sourceFileName?: string;
  concurrency?: number;
  onProgress?: (processed: number, total: number) => void;
}

const MOBILE_FIELDS = ["contact", "mobileno", "teachercontact"] as const;
const EMAIL_FIELDS = ["schoolemail", "emailid", "teacheremail"] as const;
const DATE_FIELDS = ["dateofformation"] as const;
const STATUS_ENUM = new Set(["approved", "pending", "rejected"]);

const FIELD_ALIASES: Record<string, string[]> = {
  schoolname: ["school_name"],
  contact: ["contact_number", "schoolcontact", "school_contact"],
  schoolemail: ["school_email", "email"],
  emailid: ["email_id"],
  teachercontact: ["teacher_contact"],
  dateofformation: ["date_of_formation"],
  pincode: ["pin_code"],
  kcno: ["kc_no"],
};

function normalizeMobileDigits(value: unknown): string {
  return String(value ?? "").replace(/\D/g, "");
}

/**
 * Clean a contact-number cell while preserving multiple numbers.
 * - Trims leading/trailing whitespace.
 * - Removes spaces *inside* a single number ("99999 99999" → "9999999999").
 * - Preserves separators between numbers ("/", ",", "|", newline) exactly as
 *   given ("9999999999 / 88888 88888" → "9999999999 / 8888888888").
 * - Always returns a string; never rejects a value for having multiple numbers.
 */
function normalizeContactNumbers(value: unknown): string {
  const str = String(value ?? "").trim();
  if (!str) {
    return "";
  }
  // Split into [number, separator, number, ...], keeping the separators.
  return str
    .split(/(\s*[/,|\n]\s*)/)
    .map(part =>
      // Separator chunks (contain "/", ",", "|" or newline) are kept verbatim;
      // number chunks have only their internal whitespace removed.
      /[/,|\n]/.test(part) ? part : part.replace(/\s+/g, "")
    )
    .join("");
}

function normalizeEmail(value: unknown): string {
  return String(value ?? "")
    .trim()
    .toLowerCase();
}

function getFieldValue(
  formValues: Record<string, unknown>,
  fieldKey: string
): string {
  const keys = [fieldKey, ...(FIELD_ALIASES[fieldKey] ?? [])];
  for (const key of keys) {
    const raw = formValues[key];
    if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
      return String(raw).trim();
    }
  }
  return "";
}

/** Auto-clean row data — never throws, never blocks import. */
export function smartCleanSchoolRow(
  formValues: Record<string, unknown>,
  rowNumber: number
): Record<string, unknown> {
  const cleaned: Record<string, unknown> = { ...formValues };

  for (const field of MOBILE_FIELDS) {
    const raw = getFieldValue(cleaned, field);
    if (!raw) {
      continue;
    }
    if (field === "contact") {
      // School contact may hold multiple numbers — preserve separators, only
      // strip spaces inside each number, and keep it as text (never numeric).
      const cleanedContact = normalizeContactNumbers(raw);
      cleaned[field] = cleanedContact;
      cleaned.contact_number = cleanedContact;
      cleaned.schoolcontact = cleanedContact;
      cleaned.school_contact = cleanedContact;
    } else {
      cleaned[field] = normalizeMobileDigits(raw);
    }
  }

  for (const field of EMAIL_FIELDS) {
    const raw = getFieldValue(cleaned, field);
    if (raw) {
      cleaned[field] = normalizeEmail(raw);
    }
  }

  for (const field of DATE_FIELDS) {
    const raw = getFieldValue(cleaned, field);
    if (raw) {
      // Treat date of formation as plain text — store exactly what was provided
      // (e.g. "1999") without any date parsing or conversion.
      cleaned[field] = raw;
      for (const alias of FIELD_ALIASES[field] ?? []) {
        cleaned[alias] = raw;
      }
    }
  }

  const pincode = getFieldValue(cleaned, "pincode");
  if (pincode) {
    cleaned.pincode = pincode.replace(/\D/g, "");
  }

  const statusRaw = getFieldValue(cleaned, "status").toLowerCase();
  cleaned.status = STATUS_ENUM.has(statusRaw) ? statusRaw : "approved";

  let schoolName = getFieldValue(cleaned, "schoolname");
  if (!schoolName) {
    const kcno = getFieldValue(cleaned, "kcno");
    schoolName = kcno || `Imported School (Row ${rowNumber})`;
    cleaned.schoolname = schoolName;
    cleaned.school_name = schoolName;
  }

  for (const [key, value] of Object.entries(cleaned)) {
    if (typeof value === "string") {
      cleaned[key] = value.trim();
    }
  }

  return cleaned;
}

export function extractRecordId(response: unknown): string | null {
  if (!response || typeof response !== "object") {
    return null;
  }

  const payload = response as Record<string, unknown>;
  const data =
    (payload.data as Record<string, unknown> | undefined) ??
    (payload.result as Record<string, unknown> | undefined) ??
    payload;

  const id =
    data.id ?? data._id ?? data.pk ?? payload.id ?? payload._id ?? payload.pk;

  return id !== undefined && id !== null && id !== "" ? String(id) : null;
}

/** Best-effort human-readable reason from an unknown thrown error (axios/server/Error). */
export function extractErrorMessage(error: unknown): string {
  if (!error) {
    return "Unknown error";
  }

  const axiosLike = error as {
    response?: {
      status?: number;
      data?: Record<string, unknown> | string;
    };
    message?: string;
  };

  const data = axiosLike.response?.data;
  if (data) {
    if (typeof data === "string" && data.trim()) {
      return data.trim();
    }
    if (typeof data === "object") {
      const detail =
        (data.message as string | undefined) ??
        (data.detail as string | undefined) ??
        ((data.error as Record<string, unknown> | undefined)?.message as
          | string
          | undefined) ??
        (typeof data.error === "string" ? data.error : undefined);
      if (detail) {
        return String(detail);
      }
      // Field-level validation errors → "field: msg" pairs
      const fieldErrors = Object.entries(data)
        .filter(
          ([, value]) => Array.isArray(value) || typeof value === "string"
        )
        .map(([key, value]) => {
          const text = Array.isArray(value) ? value.join(", ") : String(value);
          return `${key}: ${text}`;
        });
      if (fieldErrors.length > 0) {
        return fieldErrors.join("; ");
      }
    }
  }

  const status = axiosLike.response?.status;
  if (axiosLike.message) {
    return status ? `${axiosLike.message} (HTTP ${status})` : axiosLike.message;
  }
  if (status) {
    return `Request failed with status ${status}`;
  }

  return "Unknown error";
}

function createImportBatchId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `import-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

async function createSchoolRecord(
  schemaName: string,
  cleaned: Record<string, unknown>
): Promise<{ recordId: string | null; action: ImportRowAction }> {
  // Every imported row creates a NEW school record. We never match against
  // existing schools, so duplicate names or KC Nos each become their own
  // separate entry rather than updating or being rejected.
  const response = await DynamicModuleService.createItem(schemaName, cleaned, {
    silent: true,
  });
  return { recordId: extractRecordId(response), action: "created" };
}

async function createWithRetry(
  schemaName: string,
  cleaned: Record<string, unknown>
): Promise<{ recordId: string | null; action: ImportRowAction }> {
  try {
    return await createSchoolRecord(schemaName, cleaned);
  } catch {
    await new Promise(resolve => setTimeout(resolve, 400));
    return createSchoolRecord(schemaName, cleaned);
  }
}

export async function processSchoolImportRows(
  options: ProcessSchoolImportOptions
): Promise<ImportBatchResult> {
  const {
    schemaName = SCHOOL_PROFILE_SCHEMA_NAME,
    excelRows,
    columnMapping,
    sourceFileName,
    concurrency = DEFAULT_API_FETCH_CONCURRENCY,
    onProgress,
  } = options;

  const importBatchId = createImportBatchId();
  const startedAt = new Date().toISOString();
  const successLogs: ImportRowLog[] = [];
  const failedLogs: ImportRowLog[] = [];
  const auditRecords: SchoolImportAuditRecord[] = [];
  let processed = 0;
  let createdCount = 0;
  let updatedCount = 0;

  const rowJobs = excelRows.map((sourceRow, index) => ({
    sourceRow,
    rowNumber: index + 2,
  }));

  await mapWithConcurrency(rowJobs, concurrency, async job => {
    const { sourceRow, rowNumber } = job;

    const mappedValues = smartCleanSchoolRow(
      removeMetaFields(mapExcelRowToFormValues(sourceRow, columnMapping)),
      rowNumber
    );

    let recordId: string | null = null;
    let action: ImportRowAction = "created";
    let failureReason: string | null = null;

    try {
      const createResult = await createWithRetry(schemaName, mappedValues);
      recordId = createResult.recordId;
      action = createResult.action;
    } catch (primaryError) {
      // No stub fallback: if the row's actual data is rejected, report it as
      // FAILED with the reason and save nothing for it.
      failureReason = extractErrorMessage(primaryError);
    }

    if (failureReason) {
      failedLogs.push({
        row_number: rowNumber,
        status: "FAILED",
        error_message: failureReason,
        mapped_values: mappedValues,
        source_row: sourceRow,
      });
      processed += 1;
      onProgress?.(processed, excelRows.length);
      return;
    }

    if (action === "created") {
      createdCount += 1;
    } else {
      updatedCount += 1;
    }

    const schoolName = getFieldValue(mappedValues, "schoolname");
    const contactNumber = normalizeMobileDigits(
      getFieldValue(mappedValues, "contact")
    );
    const schoolEmail = getFieldValue(mappedValues, "schoolemail");

    try {
      await provisionSchoolLoginAccount({
        email: schoolEmail,
        phoneNumber: contactNumber,
        schoolName,
      });
    } catch {
      // Non-blocking — school record is already saved
    }

    successLogs.push({
      row_number: rowNumber,
      status: "SUCCESS",
      action,
      record_id: recordId ?? undefined,
      mapped_values: mappedValues,
      source_row: sourceRow,
    });

    const auditRecord = await persistSchoolImportAuditRecord({
      import_batch_id: importBatchId,
      row_number: rowNumber,
      action,
      record_id: recordId ?? undefined,
      created_at: new Date().toISOString(),
      source_file: sourceFileName,
    });
    auditRecords.push(auditRecord);

    processed += 1;
    onProgress?.(processed, excelRows.length);
  });

  successLogs.sort((a, b) => a.row_number - b.row_number);
  failedLogs.sort((a, b) => a.row_number - b.row_number);

  const totalRows = excelRows.length;
  const failedCount = failedLogs.length;

  return {
    summary: {
      import_batch_id: importBatchId,
      total_rows: totalRows,
      success_count: totalRows - failedCount,
      created_count: createdCount,
      updated_count: updatedCount,
      failed_count: failedCount,
      started_at: startedAt,
      finished_at: new Date().toISOString(),
    },
    success_logs: successLogs,
    failed_logs: failedLogs,
    audit_records: auditRecords,
  };
}
